/**
 * Who the server says we are: `user_id`, `is_root`, and the permission set.
 *
 * Separate from `loadShips()`, where this briefly lived, because identity is not
 * part of the plot path and the plot path is not the only route. `/admin`
 * replaces the app rather than sitting inside it, so nothing on that route ever
 * loads a ship - and the admin-only surfaces there were consequently invisible
 * to everyone, including root. Anything every route needs has to be fetched by
 * something every route mounts.
 *
 * It carries `features.catalog` for the same reason: that too was only read on
 * the Explore path, so the activity log announced "this deployment has no
 * database" to an administrator looking straight at one.
 *
 * Refreshed while signed in and when returning to the tab. A failed request
 * must not permanently cache missing permissions for the life of the page.
 */

import { getCurrentUser, getInfo } from '../api/endpoints.ts';
import { authState, initializeAuth } from '../auth/auth.ts';
import { setServerFlags } from './feature-flags.ts';
import { adoptServerPreferences } from './preferences.ts';
import { adoptStoredLens, publishLensAttribute } from './lens.ts';
import { applyStoredTheme } from '../ui/theme.ts';
import { applyChartPreferences, plotState } from './plot-state.ts';
import { runtime } from '../features/plot/plot-runtime.ts';
import {
  catalogAvailabilityKnown,
  catalogAvailable,
  fleetSummary,
  metadataUpdatedAt,
  isRootUser,
  myAccess,
  myPermissions,
  myUserId,
} from './store.ts';
import type { AccessExplanation } from '../api/types.ts';

/**
 * Take the account's stored settings.
 *
 * The mirror in `localStorage` has already seeded everything synchronously, so
 * this only matters on a browser that has never been used for this account -
 * and there it is the difference between a new laptop looking like the old one
 * and looking like a fresh install.
 *
 * Restyling a chart that is already on screen is deliberately *not* done. A
 * chart that changes its own appearance a second after it was drawn reads as a
 * bug however right the value is, so the adopted settings are applied to the
 * plot state only while nothing has been drawn and the URL is not describing a
 * chart of its own. Everything after that inherits them the ordinary way.
 */
function adoptPreferences(payload: unknown): void {
  const fresh = adoptServerPreferences(payload);
  applyStoredTheme((payload as { theme?: 'light' | 'dark' | null } | undefined)?.theme);
  // The lens the account remembers, taken only by a browser with no opinion of
  // its own - `adoptStoredLens` enforces that, the same way the theme does.
  adoptStoredLens((payload as { lens?: string | null } | undefined)?.lens);
  publishLensAttribute();
  if (!fresh || runtime.lastData) return;
  const chart = (payload as { chart?: Record<string, unknown> } | undefined)?.chart;
  plotState.set(applyChartPreferences(plotState.value, chart));
}

let inFlight: Promise<void> | null = null;

export function loadMe(): Promise<void> {
  inFlight ??= (async () => {
    await initializeAuth();
    if (authState.value.status !== 'authenticated') return;
    const user = authState.value.user;
    // Both facts every route needs: who we are, and what this deployment has.
    // `loadShips()` also reads `features`, but only on the Explore path - which
    // is why asking here as well is not redundant, it is the whole point.
    const [me, info] = await Promise.allSettled([getCurrentUser(), getInfo()]);
    // A response started before logout must not restore the old user's rights.
    if (authState.value.status !== 'authenticated' || authState.value.user !== user) return;
    if (me.status === 'fulfilled') {
      myPermissions.set(new Set(me.value.permissions ?? []));
      isRootUser.set(me.value.is_root === true);
      myUserId.set(me.value.user_id ?? null);
      myAccess.set((me.value as { access?: AccessExplanation | null }).access ?? null);
      // Which features this deployment has switched on for this account. Until
      // this lands the navigation uses the code-side defaults, which are the
      // app as it behaves with no database - so the first paint is never wrong,
      // only ever less informed. A known account means a managed policy, which
      // `?ff=` no longer overrides (the server ignores it too).
      setServerFlags((me.value as { flags?: Record<string, unknown> }).flags, me.value.user_id != null);
      adoptPreferences(me.value.preferences);
    }
    if (info.status === 'fulfilled') {
      catalogAvailable.set(info.value.features?.catalog === true);
      // Home paints before the Explore path has asked for anything, so the
      // fleet counts and the freshness stamp are taken from this boot request
      // too. `loadShips` sets them again with the same values.
      if (info.value.fleet) fleetSummary.set(info.value.fleet);
      if (info.value.metadata_updated_at) metadataUpdatedAt.set(info.value.metadata_updated_at);
    }
    // Answered either way: an `/api/info` that failed outright means nothing
    // stored can be reached right now, which is what "unavailable" says.
    catalogAvailabilityKnown.set(true);
  })().finally(() => { inFlight = null; });
  return inFlight;
}

/** Keep the signed-in account current; the app shell owns listener cleanup. */
export function watchMe(onRefresh: () => void): () => void {
  let stopped = false;
  let lastRefresh = 0;
  const refresh = (force = false): void => {
    if (authState.value.status !== 'authenticated' || document.visibilityState === 'hidden') return;
    // Focus and visibility often arrive together after waking a laptop.
    if (!force && Date.now() - lastRefresh < 10_000) return;
    lastRefresh = Date.now();
    void loadMe().then(() => { if (!stopped) onRefresh(); });
  };
  const resume = (): void => refresh();
  const reconnect = (): void => refresh(true);
  const unsubscribe = authState.subscribe((state) => {
    if (state.status === 'authenticated') refresh(true);
    else {
      myPermissions.set(new Set());
      isRootUser.set(false);
      myUserId.set(null);
      myAccess.set(null);
      catalogAvailable.set(false);
      catalogAvailabilityKnown.set(false);
      setServerFlags({});
    }
  });
  window.addEventListener('focus', resume);
  window.addEventListener('online', reconnect);
  document.addEventListener('visibilitychange', resume);
  const timer = window.setInterval(resume, 60_000);
  refresh(true);
  return () => {
    stopped = true;
    unsubscribe();
    window.clearInterval(timer);
    window.removeEventListener('focus', resume);
    window.removeEventListener('online', reconnect);
    document.removeEventListener('visibilitychange', resume);
  };
}
